import { useState, useRef, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Check, Search, X, Plus, type LucideIcon } from 'lucide-react';

export interface SelectOption {
  value: string;
  label: string;
  subLabel?: string;
  disabled?: boolean;
}

export interface SelectAction {
  label: string;
  onAction: () => void;
  icon?: LucideIcon;
}

export interface SelectProps {
  value?: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  disabled?: boolean;
  searchable?: boolean;
  className?: string;
  buttonClassName?: string;
  error?: boolean | string;
  id?: string;
  size?: 'sm' | 'md';
  'aria-label'?: string;
  icon?: LucideIcon;
  action?: SelectAction;
}

/**
 * Premium Custom Dropdown Component with smooth animated floating popover,
 * real-time search, checkmark indicators, and robust keyboard accessibility.
 */
export default function Select({
  value = '',
  onChange,
  options,
  placeholder = 'Select option…',
  disabled = false,
  searchable,
  className = '',
  buttonClassName = '',
  error,
  id,
  size = 'md',
  'aria-label': ariaLabel,
  icon: IconComponent,
  action,
}: SelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Auto-enable search if there are more than 7 options unless explicitly specified
  const isSearchable = searchable ?? options.length > 7;

  // Selected option label
  const selectedOption = useMemo(() => {
    return options.find((o) => o.value === value);
  }, [options, value]);

  // Filter options based on search query
  const filteredOptions = useMemo(() => {
    if (!query.trim()) return options;
    const q = query.toLowerCase().trim();
    return options.filter(
      (o) =>
        o.label.toLowerCase().includes(q) ||
        (o.subLabel && o.subLabel.toLowerCase().includes(q)),
    );
  }, [options, query]);

  // Handle outside click to close dropdown
  useEffect(() => {
    if (!open) return;
    function handleClickOutside(e: MouseEvent | TouchEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  // Focus search input when popover opens
  useEffect(() => {
    if (open && isSearchable) {
      const timer = setTimeout(() => {
        searchInputRef.current?.focus();
      }, 50);
      return () => clearTimeout(timer);
    } else {
      setQuery('');
    }
  }, [open, isSearchable]);

  const handleSelect = (val: string) => {
    onChange(val);
    setOpen(false);
  };

  const isSmall = size === 'sm';

  return (
    <div
      ref={containerRef}
      className={`vns-select ${open ? 'is-open' : ''} ${error ? 'is-error' : ''} ${
        isSmall ? 'size-sm' : 'size-md'
      } ${className}`}
    >
      {/* Trigger Button */}
      <button
        id={id}
        type="button"
        disabled={disabled}
        aria-label={ariaLabel || placeholder}
        aria-expanded={open}
        aria-haspopup="listbox"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (!disabled) setOpen((prev) => !prev);
        }}
        className={`vns-select-trigger ${buttonClassName}`}
      >
        <div className="vns-select-label-wrap">
          {IconComponent && (
            <IconComponent size={isSmall ? 13 : 15} style={{ color: 'var(--text-faint)', flexShrink: 0 }} />
          )}
          {selectedOption ? (
            <span className="vns-select-value">{selectedOption.label}</span>
          ) : (
            <span className="vns-select-placeholder">{placeholder}</span>
          )}
        </div>

        <div className="vns-select-chevron">
          <ChevronDown size={isSmall ? 13 : 15} />
        </div>
      </button>

      {/* Floating Animated Menu Popover */}
      <AnimatePresence>
        {open && (
          <motion.div
            role="listbox"
            initial={{ opacity: 0, y: -4, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -3, scale: 0.98 }}
            transition={{ duration: 0.14, ease: [0.16, 1, 0.3, 1] }}
            className="vns-select-popover"
          >
            {/* Optional Search Filter Bar */}
            {isSearchable && (
              <div className="vns-select-search-wrap">
                <div className="vns-select-search-icon">
                  <Search size={13} />
                </div>
                <input
                  ref={searchInputRef}
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search options..."
                  className="vns-select-search-input"
                />
                {query && (
                  <button
                    type="button"
                    onClick={() => setQuery('')}
                    className="vns-select-search-clear"
                    title="Clear search"
                  >
                    <X size={12} />
                  </button>
                )}
              </div>
            )}

            {/* Optional Action Banner (e.g. + Add New Sub Contractor) */}
            {action && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setOpen(false);
                  action.onAction();
                }}
                className="vns-select-action-btn"
                style={{
                  width: '100%',
                  padding: '9.5px 14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  background: '#eff6ff',
                  border: 'none',
                  borderBottom: '1px solid #dbeafe',
                  color: '#1d4ed8',
                  fontWeight: 700,
                  fontSize: 13,
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'background 0.15s ease',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#dbeafe'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = '#eff6ff'; }}
              >
                {action.icon ? (
                  <action.icon size={15} strokeWidth={2.4} />
                ) : (
                  <Plus size={15} strokeWidth={2.4} />
                )}
                <span>{action.label}</span>
              </button>
            )}

            {/* Options Scrollable List */}
            <div className="vns-select-options">
              {filteredOptions.length === 0 ? (
                <div className="vns-select-empty">No matching options</div>
              ) : (
                filteredOptions.map((opt) => {
                  const isSelected = opt.value === value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      disabled={opt.disabled}
                      onClick={() => handleSelect(opt.value)}
                      className={`vns-select-option ${isSelected ? 'is-selected' : ''}`}
                    >
                      <div className="vns-select-option-text">
                        <span className="vns-select-option-label">{opt.label}</span>
                        {opt.subLabel && (
                          <span className="vns-select-option-sublabel">{opt.subLabel}</span>
                        )}
                      </div>

                      {isSelected && (
                        <div className="vns-select-check">
                          <Check size={14} strokeWidth={2.5} />
                        </div>
                      )}
                    </button>
                  );
                })
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
